import type { ReactNode } from 'react';
import { Link, usePage } from '@inertiajs/react';

import { ContentContainer } from '@/components/ui/content-container';
import { Heading4 } from '@/components/ui/typography';
import { useDerivedContentTone } from '@/hooks/use-derived-content-tone';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { SECTION_BACKGROUND_CONTENT_TONE_CLASSNAMES } from '@/lib/derive-content-tone';
import { cn } from '@/lib/utils';
import type { PageBlock } from '@/types/block';
import {
  activeFilter,
  filterHref,
  type IndexFilterMode,
  type IndexFilterOption,
  type IndexLayoutProps,
} from '../index-section-utils';

const FILTER_NAV_CLASS: Record<IndexFilterMode, string> = {
  tabs: 'overflow-x-auto',
  vertical: '',
  center: 'flex justify-center',
};

const FILTER_CONTAINER_CLASS: Record<IndexFilterMode, string> = {
  tabs: 'flex min-w-min items-center gap-6 border-b border-border',
  vertical: 'flex flex-wrap gap-2 lg:flex-col lg:items-stretch lg:gap-1 lg:[&>*]:w-full',
  center: 'flex flex-wrap items-center justify-center gap-2',
};

export interface IndexSectionFilters {
  show: boolean;
  limit?: number;
  items: IndexFilterOption[];
  key: string;
  label: string;
  block?: PageBlock;
}

export interface IndexSectionCollection {
  hasVisibleRecords: boolean;
  hasCollectionBlocks: boolean;
  body: ReactNode;
  pagination: ReactNode;
  heading?: string;
}

export interface IndexSectionPinnedCollection {
  show: boolean;
  hasCollectionBlocks: boolean;
  body: ReactNode;
  fullWidth?: boolean;
  presentation?: 'featuredOverlay';
}

export interface IndexSectionViewProps {
  layoutProps: IndexLayoutProps;
  sectionBackgroundColor?: string;
  filters: IndexSectionFilters;
  pinnedCollection?: IndexSectionPinnedCollection;
  collection: IndexSectionCollection;
}

export function IndexSectionBackgroundShell({
  sectionBackgroundColor,
  children,
}: {
  sectionBackgroundColor?: string;
  children: ReactNode;
}) {
  const derivedTone = useDerivedContentTone(sectionBackgroundColor);
  const toneClassName = derivedTone
    ? SECTION_BACKGROUND_CONTENT_TONE_CLASSNAMES[derivedTone]
    : undefined;

  return (
    <div
      className={cn('flex flex-col bg-background', toneClassName)}
      style={sectionBackgroundColor ? { backgroundColor: sectionBackgroundColor } : undefined}
    >
      {children}
    </div>
  );
}

export function IndexSectionContainer({
  layoutProps,
  children,
  className,
}: {
  layoutProps: IndexLayoutProps;
  children: ReactNode;
  className?: string;
}) {
  const constrainedMaxWidthClass =
    layoutProps.contentMaxWidth === '4xl'
      ? 'mx-auto w-full max-w-4xl px-4 lg:px-8'
      : layoutProps.contentMaxWidth === '5xl'
        ? 'mx-auto w-full max-w-5xl px-4 lg:px-8'
        : layoutProps.contentMaxWidth === '6xl'
          ? 'mx-auto w-full max-w-6xl px-4 lg:px-8'
          : undefined;

  return (
    <ContentContainer
      passthrough={layoutProps.fullWidth || Boolean(constrainedMaxWidthClass)}
      className={cn(
        'flex w-full flex-col not-last:pb-0 py-8',
        layoutProps.fullWidth ? 'px-4 lg:px-8' : (constrainedMaxWidthClass ?? 'container'),
        className,
      )}
    >
      {children}
    </ContentContainer>
  );
}

export function IndexSectionFilterTabs({
  showFilters,
  filterItems,
  filterKey,
  filterLabel,
  filterLimit,
  filterBlock,
  mode = 'tabs',
}: {
  showFilters: boolean;
  filterItems: IndexFilterOption[];
  filterKey: string;
  filterLabel: string;
  filterLimit?: number;
  filterBlock?: PageBlock;
  mode?: IndexFilterMode;
}) {
  if (!showFilters) return null;

  const tabs = (
    <IndexFilterTabs
      items={filterItems}
      filterKey={filterKey}
      label={filterLabel}
      limit={filterLimit}
      mode={mode}
    />
  );

  if (filterBlock) {
    return (
      <div data-index-block-id={filterBlock.id} data-index-block-role="index_filters">
        {tabs}
      </div>
    );
  }

  return tabs;
}

function IndexFilterTabs({
  items,
  filterKey,
  label,
  limit,
  mode = 'tabs',
}: {
  items: IndexFilterOption[];
  filterKey: string;
  label: string;
  limit?: number;
  mode?: IndexFilterMode;
}) {
  const { url } = usePage();
  const isPageEditor = useIsPageEditor();
  const filterableItems = items.filter((item) => item.name);
  const visibleItems = filterableItems.slice(0, Math.max(limit ?? 5, 0));
  if (visibleItems.length === 0) return null;

  const active = activeFilter(url, filterKey);

  return (
    <nav aria-label={label} className={FILTER_NAV_CLASS[mode]}>
      <div className={FILTER_CONTAINER_CLASS[mode]}>
        <IndexFilterControl
          href={filterHref(url, filterKey, null)}
          disabled={isPageEditor}
          active={active === ''}
          mode={mode}
        >
          View All
        </IndexFilterControl>
        {visibleItems.map((item) => {
          const value = item.name || '';
          return (
            <IndexFilterControl
              key={item.id || value}
              href={filterHref(url, filterKey, value)}
              disabled={isPageEditor}
              active={active === value.toLowerCase()}
              mode={mode}
            >
              {value}
            </IndexFilterControl>
          );
        })}
      </div>
    </nav>
  );
}

function IndexFilterControl({
  href,
  disabled,
  active,
  mode,
  children,
}: {
  href: string;
  disabled: boolean;
  active: boolean;
  mode: IndexFilterMode;
  children: ReactNode;
}) {
  const className = filterLinkClass(active, disabled, mode);

  if (disabled) {
    return <span className={className}>{children}</span>;
  }

  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  );
}

function filterLinkClass(active: boolean, disabled: boolean, mode: IndexFilterMode) {
  const base = 'inline-flex items-center text-sm transition-colors min-h-9 font-medium';

  if (mode === 'tabs') {
    return cn(
      base,
      'relative px-1 py-3 whitespace-nowrap',
      'after:absolute after:inset-x-0 after:bottom-0 after:z-10 after:h-0.5 after:bg-primary after:origin-left after:transition-transform after:duration-200',
      disabled && 'cursor-default',
      active ? 'text-primary after:scale-x-100' : 'text-inherit after:scale-x-0',
    );
  }

  const button = cn(
    base,
    'rounded-md px-3 py-2',
    disabled && 'cursor-default',
    active
      ? 'bg-primary text-primary-foreground'
      : cn('text-inherit', !disabled && 'hover:bg-primary hover:text-primary-foreground'),
  );

  return button;
}

export function IndexSectionCollectionHeading({ heading }: { heading?: string }) {
  if (!heading) return null;

  return <Heading4 className="text-sm tracking-wide">{heading.toUpperCase()}</Heading4>;
}

export function shouldShowPinnedCollection(pinnedCollection?: IndexSectionPinnedCollection) {
  return Boolean(
    pinnedCollection?.show && pinnedCollection.hasCollectionBlocks && pinnedCollection.body,
  );
}

export function shouldShowCollection(collection: IndexSectionCollection) {
  return collection.hasVisibleRecords && collection.hasCollectionBlocks;
}
